import { useEffect, useState } from "react";
import { AdminShell as Layout } from "@/components/Layout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Warning, Sparkle, Storefront, CurrencyDollar, Calendar, Trash } from "@phosphor-icons/react";

/** Receipt Purgatory — triage lane for photos misattached to a WO */
export default function AdminReceiptsPurgatory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceWoId, setSourceWoId] = useState("");
  const [assigning, setAssigning] = useState(null); // id being reassigned
  const [pickerOpen, setPickerOpen] = useState(null); // receipt.id
  const [ocring, setOcring] = useState(null); // id being OCR-scanned
  const [bulkOcring, setBulkOcring] = useState(false);
  const [magnets, setMagnets] = useState([]);        // WOs w/ 50+ photos
  const [detachTarget, setDetachTarget] = useState(null); // {wo_id,total,wo}
  const [detachConfirm, setDetachConfirm] = useState("");
  const [detaching, setDetaching] = useState(false);
  const [detachAllOpen, setDetachAllOpen] = useState(false);
  const [detachAllConfirm, setDetachAllConfirm] = useState("");
  const [detachingAll, setDetachingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = sourceWoId ? `?source_wo_id=${sourceWoId}` : "";
      const [q, m] = await Promise.all([
        api.get(`/receipts/purgatory${params}`),
        api.get("/receipts/magnet-wos").catch(() => ({ data: { rows: [] } })),
      ]);
      setRows(q.data.rows || []);
      setMagnets(m.data.rows || []);
    } catch (e) {
      toast.error("Failed to load");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [sourceWoId]);

  const runDetach = async () => {
    if (!detachTarget) return;
    if (detachConfirm.trim() !== `DETACH ${detachTarget.total}`) {
      toast.error(`Type "DETACH ${detachTarget.total}" exactly to confirm`);
      return;
    }
    setDetaching(true);
    try {
      const r = await api.post("/receipts/detach-magnet-wo", {
        source_wo_id: detachTarget.wo_id,
        confirm_count: detachTarget.total,
      });
      toast.success(`Detached ${r.data.detached} photos from WO#${r.data.wo}. Retriage them from the WhatsApp Unmatched tab.`);
      setDetachTarget(null);
      setDetachConfirm("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Detach failed");
    } finally { setDetaching(false); }
  };

  // Sum across every listed magnet WO — drives the "Detach ALL" confirm
  // token and the running total in the header.
  const magnetsTotal = magnets.reduce((s, m) => s + (m.total || 0), 0);

  const runDetachAll = async () => {
    if (detachAllConfirm.trim() !== `NUKE ${magnetsTotal}`) {
      toast.error(`Type "NUKE ${magnetsTotal}" exactly to confirm`);
      return;
    }
    setDetachingAll(true);
    try {
      const r = await api.post("/receipts/detach-all-magnets", {
        confirm_total: magnetsTotal,
      });
      toast.success(`Detached ${r.data.detached} photos across ${r.data.wos_processed} magnet WOs. Retriage them from the WhatsApp Unmatched tab.`);
      setDetachAllOpen(false);
      setDetachAllConfirm("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Bulk detach failed");
    } finally { setDetachingAll(false); }
  };

  const resolveUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    if (u.startsWith("/api/")) return `${API.replace(/\/api$/, "")}${u}`;
    return `${API}${u.startsWith("/") ? "" : "/"}${u}`;
  };

  const reassign = async (receiptId, targetWoId, targetBucket) => {
    setAssigning(receiptId);
    try {
      const r = await api.post("/receipts/reassign", {
        id: receiptId, target_wo_id: targetWoId, target_bucket: targetBucket,
      });
      toast.success(`Moved to WO#${r.data.moved_to.wo}`);
      setRows(rows.filter(x => x.id !== receiptId));
      setPickerOpen(null);
    } catch (e) {
      toast.error("Move failed");
    } finally { setAssigning(null); }
  };

  const ocrOne = async (receiptId) => {
    setOcring(receiptId);
    try {
      const r = await api.post("/receipts/ocr", { id: receiptId });
      const ocr = r.data.ocr || {};
      setRows((prev) => prev.map(x => x.id === receiptId ? { ...x, ocr } : x));
      const bits = [ocr.vendor, ocr.amount && `$${ocr.amount}`, ocr.date].filter(Boolean).join(" · ");
      toast.success(bits ? `Read: ${bits}` : "Nothing legible");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "OCR failed"));
    } finally { setOcring(null); }
  };

  const ocrAllVisible = async () => {
    if (!window.confirm(`Auto-read ${rows.filter(r => !r.ocr).length} un-scanned receipts with AI? (each ≈$0.01)`)) return;
    setBulkOcring(true);
    let done = 0, failed = 0;
    for (const r of rows) {
      if (r.ocr) continue;
      try {
        const res = await api.post("/receipts/ocr", { id: r.id });
        const ocr = res.data.ocr || {};
        setRows((prev) => prev.map(x => x.id === r.id ? { ...x, ocr } : x));
        done += 1;
      } catch (e) {
        failed += 1;
      }
    }
    setBulkOcring(false);
    toast.success(`OCR done: ${done} read · ${failed} failed`);
  };

  const unscannedCount = rows.filter(r => !r.ocr).length;
  const suggested = rows.filter(r => r.ocr?.suggestion?.wo_id);
  const highCount = suggested.filter(r => r.ocr.suggestion.confidence === "high").length;

  const acceptSuggestions = async (ids, min_confidence) => {
    if (!ids.length) return;
    if (ids.length > 1 && !window.confirm(`Move ${ids.length} receipts to their AI-suggested WOs?`)) return;
    setAssigning("bulk");
    try {
      const r = await api.post("/receipts/accept-suggestions", { ids, min_confidence });
      toast.success(`Moved ${r.data.moved} receipts to ${r.data.target_wo_count} WOs${r.data.skipped ? ` · ${r.data.skipped} skipped` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Accept failed"));
    } finally { setAssigning(null); }
  };

  return (
    <Layout>
    <div className="p-4 max-w-6xl mx-auto space-y-4" data-testid="receipts-purgatory-page">
      <div className="flex items-start gap-2 flex-wrap">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-orange-700 flex items-center gap-1">
            <Warning weight="fill" size={12}/> Photo Triage
          </div>
          <h1 className="text-2xl font-black tracking-tight">Receipt Purgatory</h1>
          <p className="text-sm text-slate-600 mt-1">
            Photos stuck on WOs with 50+ receipts (usually the WhatsApp import catch-all).
            Tap <b>Auto-Read</b> to let Claude Vision pull vendor + amount + date, then <b>Reassign</b> to the correct WO.
          </p>
        </div>
        <div className="ml-auto text-right">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">In Queue</div>
          <div className="text-3xl font-black">{rows.length}</div>
        </div>
      </div>

      {/* Magnet WOs — the 3-4 "catch-all" tickets that have hundreds of
          receipts piled on them. One-click nuke drops every photo into
          the WhatsApp Unmatched staging for proper retriage. */}
      {magnets.length > 0 && (
        <div className="border-2 border-red-500 bg-red-50 rounded-md p-3 space-y-2" data-testid="magnet-wos-panel">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-red-800 flex items-center gap-1">
                <Warning weight="fill" size={12}/> Magnet WOs · {magnetsTotal} total photos
              </div>
              <div className="text-xs text-slate-700 font-medium mt-0.5">
                These {magnets.length} work order{magnets.length === 1 ? "" : "s"} have absorbed hundreds of receipts through the WhatsApp / Kraaft auto-match. Detach the entire pile in one click — every photo lands in the <b>WhatsApp Unmatched</b> tab for proper retriage.
              </div>
            </div>
            {magnets.length > 1 && (
              <button onClick={() => { setDetachAllOpen(true); setDetachAllConfirm(""); }}
                data-testid="magnet-detach-all-btn"
                className="h-9 px-4 bg-red-700 hover:bg-red-800 text-white font-black uppercase text-[11px] tracking-widest rounded inline-flex items-center gap-1.5 shadow-lg shadow-red-500/40 ring-2 ring-red-300">
                <Trash weight="fill" size={12}/> Detach ALL magnets ({magnetsTotal})
              </button>
            )}
          </div>
          <div className="space-y-1.5">
            {magnets.map(m => (
              <div key={m.wo_id}
                data-testid={`magnet-wo-${m.wo_id}`}
                className="flex items-center gap-3 bg-white border border-red-200 rounded p-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-black tabular-nums">
                    WO#{m.work_order_number || m.wo_id.slice(-6)} · <span className="text-red-700">{m.total} photos</span>
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {m.property_address || m.title || "—"} · {m.status || "open"}
                  </div>
                  <div className="text-[9px] text-slate-400 mt-0.5">
                    before {m.before_n} · after {m.after_n} · receipts {m.receipt_n}
                  </div>
                </div>
                <button onClick={() => { setSourceWoId(m.wo_id === sourceWoId ? "" : m.wo_id); }}
                  data-testid={`magnet-view-${m.wo_id}`}
                  className="h-8 px-3 border-2 border-slate-400 bg-white hover:bg-slate-100 text-slate-700 font-black uppercase text-[10px] tracking-widest rounded">
                  {sourceWoId === m.wo_id ? "Showing" : "View"}
                </button>
                <button onClick={() => { setDetachTarget(m); setDetachConfirm(""); }}
                  data-testid={`magnet-detach-${m.wo_id}`}
                  className="h-8 px-3 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center gap-1">
                  <Trash weight="fill" size={11}/> Detach all {m.total}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {sourceWoId && (
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <span>Filtered to WO {sourceWoId.slice(-6)}.</span>
          <button onClick={() => setSourceWoId("")}
            data-testid="magnet-view-clear"
            className="underline text-slate-500">clear filter</button>
        </div>
      )}

      {rows.length > 0 && unscannedCount > 0 && (
        <div className="flex items-center gap-3 flex-wrap p-3 border-2 border-purple-300 bg-purple-50 rounded-md">
          <Sparkle weight="fill" size={16} className="text-purple-600 shrink-0"/>
          <div className="text-xs text-slate-700 font-medium">
            <b>{unscannedCount}</b> un-scanned receipts. Bulk-read them all with Claude Vision to grab vendor/amount/date at once.
          </div>
          <button onClick={ocrAllVisible} disabled={bulkOcring}
            data-testid="bulk-ocr-btn"
            className="ml-auto h-9 px-4 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-black uppercase text-[11px] tracking-widest rounded inline-flex items-center gap-1.5">
            <Sparkle weight="fill" size={12}/>
            {bulkOcring ? "Reading…" : `Auto-Read All (${unscannedCount})`}
          </button>
        </div>
      )}

      {suggested.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap p-3 border-2 border-emerald-400 bg-emerald-50 rounded-md" data-testid="suggestions-bar">
          <Sparkle weight="fill" size={16} className="text-emerald-600 shrink-0"/>
          <div className="text-xs text-slate-700 font-medium">
            AI matched <b>{suggested.length}</b> receipts to a WO (address / WO# read off the receipt) — <b>{highCount}</b> high confidence.
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={() => acceptSuggestions(suggested.filter(r => r.ocr.suggestion.confidence === "high").map(r => r.id), "high")}
              disabled={assigning === "bulk" || !highCount} data-testid="accept-high-btn"
              className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black uppercase text-[11px] tracking-widest rounded">
              Accept {highCount} High
            </button>
            <button onClick={() => acceptSuggestions(suggested.map(r => r.id), "medium")}
              disabled={assigning === "bulk"} data-testid="accept-all-btn"
              className="h-9 px-4 border-2 border-emerald-600 bg-white hover:bg-emerald-100 disabled:opacity-50 text-emerald-800 font-black uppercase text-[11px] tracking-widest rounded">
              Accept All {suggested.length}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-slate-500 border-2 border-dashed border-slate-300 rounded">
          Nothing in purgatory. Every WO has &lt;50 photos.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {rows.map(r => (
            <div key={r.id} data-testid={`receipt-${r.id}`}
              className="border-2 border-slate-200 bg-white rounded overflow-hidden">
              <a href={resolveUrl(r.url || r.path)} target="_blank" rel="noreferrer"
                className="block aspect-square bg-slate-100">
                <img src={resolveUrl(r.thumb_url || r.url || r.path)} alt="" className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.opacity = 0.3; }}/>
              </a>
              <div className="p-2 text-[10px] space-y-1">
                <div className="text-slate-500">
                  <span className="font-mono">{r.bucket.replace("_photos", "")}</span> · from WO#{r.source_wo_number || r.source_wo_id.slice(-6)}
                </div>
                {r.uploaded_at && <div className="text-slate-400">{(r.uploaded_at || "").slice(0, 10)}</div>}
                {r.sender && <div className="text-slate-500 truncate">by {r.sender}</div>}

                {/* OCR result panel */}
                {r.ocr ? (
                  <div className="border border-purple-300 bg-purple-50 rounded p-1.5 space-y-0.5"
                    data-testid={`ocr-result-${r.id}`}>
                    <div className="flex items-center gap-1 text-[11px] font-black">
                      <Storefront weight="fill" size={10} className="text-purple-600"/>
                      <span className="truncate">{r.ocr.vendor || <span className="text-slate-400 font-normal">no vendor</span>}</span>
                    </div>
                    {(r.ocr.amount || r.ocr.date) && (
                      <div className="flex items-center gap-2 text-[10px] text-slate-700 font-medium">
                        {r.ocr.amount && (
                          <span className="inline-flex items-center gap-0.5">
                            <CurrencyDollar weight="bold" size={10}/>{r.ocr.amount}
                          </span>
                        )}
                        {r.ocr.date && (
                          <span className="inline-flex items-center gap-0.5">
                            <Calendar weight="bold" size={10}/>{r.ocr.date}
                          </span>
                        )}
                      </div>
                    )}
                    {r.ocr.category && (
                      <div className="text-[9px] uppercase tracking-widest text-purple-700 font-black">{r.ocr.category}</div>
                    )}
                    {(r.ocr.address || r.ocr.wo_number) && (
                      <div className="text-[9px] text-slate-600 truncate">on receipt: {[r.ocr.wo_number && `WO ${r.ocr.wo_number}`, r.ocr.address, r.ocr.unit].filter(Boolean).join(" · ")}</div>
                    )}
                    {r.ocr.suggestion?.wo_id ? (
                      <button onClick={() => acceptSuggestions([r.id], "medium")} disabled={assigning}
                        data-testid={`accept-suggestion-${r.id}`}
                        title={r.ocr.suggestion.reason}
                        className={`w-full mt-1 h-7 rounded font-black uppercase text-[9px] tracking-widest text-white ${r.ocr.suggestion.confidence === "high" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-500 hover:bg-amber-600"}`}>
                        → WO#{r.ocr.suggestion.wo} {r.ocr.suggestion.confidence === "high" ? "✓" : "?"}
                      </button>
                    ) : (
                      <div className="text-[9px] text-slate-400">no WO match — pick manually</div>
                    )}
                  </div>
                ) : (
                  <button onClick={() => ocrOne(r.id)}
                    disabled={ocring === r.id || bulkOcring}
                    data-testid={`receipt-ocr-${r.id}`}
                    className="w-full h-7 border-2 border-purple-400 bg-white hover:bg-purple-50 disabled:opacity-50 text-purple-700 font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center justify-center gap-1">
                    <Sparkle weight="fill" size={10}/>
                    {ocring === r.id ? "Reading…" : "Auto-Read"}
                  </button>
                )}

                <button onClick={() => setPickerOpen(pickerOpen === r.id ? null : r.id)}
                  data-testid={`receipt-reassign-${r.id}`}
                  disabled={assigning === r.id}
                  className="w-full h-8 mt-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-black uppercase text-[10px] tracking-widest rounded">
                  {assigning === r.id ? "Moving…" : "Reassign →"}
                </button>
                {pickerOpen === r.id && (
                  <WoPicker onPick={(woId, bucket) => reassign(r.id, woId, bucket)}
                    onCancel={() => setPickerOpen(null)}
                    seedQuery={r.ocr?.address || r.ocr?.wo_number || ""}/>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detach confirm modal — forces admin to type "DETACH N" so nobody
          nukes a WO by accidental click. Every photo lands in the WhatsApp
          Unmatched staging, so this is reversible in principle (the
          storage bytes are never deleted). */}
      {detachTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !detaching && setDetachTarget(null)}
          data-testid="detach-modal-backdrop">
          <div onClick={(e) => e.stopPropagation()}
            className="bg-white border-4 border-red-600 rounded-lg max-w-md w-full p-5 space-y-3"
            data-testid="detach-modal">
            <div className="flex items-center gap-2">
              <Trash weight="fill" size={20} className="text-red-600"/>
              <h2 className="text-lg font-black tracking-tight">
                Detach {detachTarget.total} photos?
              </h2>
            </div>
            <div className="text-sm text-slate-700 leading-relaxed">
              Every photo attached to <b>WO#{detachTarget.work_order_number || detachTarget.wo_id.slice(-6)}</b> ({detachTarget.property_address || detachTarget.title || "—"}) will be pulled off the WO and dropped into the <b>WhatsApp Unmatched</b> tab.
              <div className="mt-2 text-xs text-slate-500">
                Nothing is deleted — the storage bytes stay put. You can reattach each photo to the correct WO from the Unmatched tab (with AI-suggest).
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-red-700">
                Type <span className="font-mono bg-red-100 px-1 rounded">DETACH {detachTarget.total}</span> to confirm
              </label>
              <input value={detachConfirm} onChange={(e) => setDetachConfirm(e.target.value)}
                autoFocus disabled={detaching}
                data-testid="detach-modal-confirm-input"
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 focus:border-red-500 rounded text-sm font-mono"
                placeholder={`DETACH ${detachTarget.total}`}/>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => { setDetachTarget(null); setDetachConfirm(""); }}
                disabled={detaching}
                data-testid="detach-modal-cancel"
                className="flex-1 h-10 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase text-xs tracking-widest rounded disabled:opacity-50">
                Cancel
              </button>
              <button onClick={runDetach}
                disabled={detaching || detachConfirm.trim() !== `DETACH ${detachTarget.total}`}
                data-testid="detach-modal-confirm-btn"
                className="flex-1 h-10 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center justify-center gap-1.5">
                <Trash weight="fill" size={13}/>
                {detaching ? "Detaching…" : `Detach ${detachTarget.total}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk-detach all magnets. Higher friction than single-WO modal
          (typing NUKE not just DETACH) because this can touch thousands
          of photos across several WOs in one call. */}
      {detachAllOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !detachingAll && setDetachAllOpen(false)}
          data-testid="detach-all-modal-backdrop">
          <div onClick={(e) => e.stopPropagation()}
            className="bg-white border-4 border-red-700 rounded-lg max-w-lg w-full p-5 space-y-3 shadow-2xl shadow-red-500/40"
            data-testid="detach-all-modal">
            <div className="flex items-center gap-2">
              <Trash weight="fill" size={22} className="text-red-700"/>
              <h2 className="text-xl font-black tracking-tight text-red-900">
                Detach ALL {magnetsTotal} photos across {magnets.length} magnet WOs?
              </h2>
            </div>
            <div className="text-sm text-slate-700 leading-relaxed">
              This will pull every photo off <b>all {magnets.length}</b> magnet WOs in one shot:
              <ul className="mt-2 space-y-0.5 text-xs bg-red-50 border border-red-200 rounded p-2 max-h-40 overflow-y-auto">
                {magnets.map(m => (
                  <li key={m.wo_id} className="flex justify-between font-mono">
                    <span>WO#{m.work_order_number || m.wo_id.slice(-6)}</span>
                    <span className="font-black">{m.total} photos</span>
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-xs text-slate-500">
                Every photo lands in the <b>WhatsApp Unmatched</b> tab, ready for AI-suggest + attach. Nothing is deleted — storage bytes are preserved and full <code>original_photo</code> snapshots are kept for recovery.
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-red-700">
                Type <span className="font-mono bg-red-100 px-1 rounded">NUKE {magnetsTotal}</span> to confirm
              </label>
              <input value={detachAllConfirm} onChange={(e) => setDetachAllConfirm(e.target.value)}
                autoFocus disabled={detachingAll}
                data-testid="detach-all-modal-confirm-input"
                className="mt-1 w-full h-10 px-3 border-2 border-slate-300 focus:border-red-600 rounded text-sm font-mono"
                placeholder={`NUKE ${magnetsTotal}`}/>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => { setDetachAllOpen(false); setDetachAllConfirm(""); }}
                disabled={detachingAll}
                data-testid="detach-all-modal-cancel"
                className="flex-1 h-10 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase text-xs tracking-widest rounded disabled:opacity-50">
                Cancel
              </button>
              <button onClick={runDetachAll}
                disabled={detachingAll || detachAllConfirm.trim() !== `NUKE ${magnetsTotal}`}
                data-testid="detach-all-modal-confirm-btn"
                className="flex-1 h-10 bg-red-700 hover:bg-red-800 disabled:bg-red-300 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center justify-center gap-1.5">
                <Trash weight="fill" size={13}/>
                {detachingAll ? "Nuking…" : `Nuke ALL ${magnetsTotal}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Layout>
  );
}

function WoPicker({ onPick, onCancel, seedQuery = "" }) {
  const [q, setQ] = useState(seedQuery);
  const [results, setResults] = useState([]);
  const [bucket, setBucket] = useState("receipts");
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/receipts/wo-search?q=${encodeURIComponent(q)}`);
        setResults(r.data.rows || []);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="mt-2 border-2 border-orange-500 bg-orange-50 rounded p-2 space-y-2">
      <input list="known-addresses" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
        placeholder="Search WO# / address / vendor…"
        data-testid="wo-picker-search"
        className="w-full h-8 px-2 border border-slate-300 rounded text-[11px] font-medium"/>
      <div className="flex gap-1">
        {["receipts", "before_photos", "after_photos"].map(b => (
          <button key={b} onClick={() => setBucket(b)}
            data-testid={`bucket-${b}`}
            className={`flex-1 h-6 text-[9px] font-black uppercase tracking-widest rounded ${bucket === b ? "bg-orange-600 text-white" : "bg-white border border-slate-300 text-slate-600"}`}>
            {b.replace("_photos", "")}
          </button>
        ))}
      </div>
      {results.length > 0 && (
        <div className="max-h-40 overflow-y-auto border border-slate-200 rounded bg-white">
          {results.map(r => (
            <button key={r.id} onClick={() => onPick(r.id, bucket)}
              data-testid={`wo-pick-${r.id}`}
              className="block w-full text-left px-2 py-1.5 border-b border-slate-100 hover:bg-orange-100 text-[10px]">
              <span className="font-mono font-black">WO#{r.wo || r.id.slice(-6)}</span>
              <span className="ml-1 text-slate-500">{r.title}</span>
              <div className="text-[9px] text-slate-400">{r.address}{r.unit ? ` · ${r.unit}` : ""} · {r.status}</div>
            </button>
          ))}
        </div>
      )}
      <button onClick={onCancel} className="text-[9px] text-slate-500 underline">cancel</button>
    </div>
  );
}
